import React, { useContext, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  useHMSStore,
  useHMSNotifications,
  HMSNotificationTypes,
  Text,
  PoorConnectivityIcon,
  ConnectivityIcon,
  PersonIcon,
  Button,
  isMobileDevice,
  useHMSActions,
} from "@100mslive/hms-video-react";
import { selectIsLocalScreenShared, selectLocalPeer } from "@100mslive/hms-video-store";
import { HMSToastContainer, hmsToast } from "./hms-toast";
import { TrackUnmuteModal } from "./TrackUnmuteModal";
import { AutoplayBlockedModal } from "./AutoplayBlockedModal";
import { PermissionErrorModal } from "./PermissionErrorModal";
import { AppContext } from "../../../store/AppContext";
import { TrackMuteAllModal } from "./TrackMuteAllModal";
import { pluralize } from "../../../common/utils";
import { ROLES } from '../../../common/roles';

export function Notifications() {
  const notification = useHMSNotifications();
  const hmsActions = useHMSActions();
  const history = useHistory();
  const params = useParams();
  const isLocalScreenShared = useHMSStore(selectIsLocalScreenShared);
  const { loginInfo, subscribedNotifications } = useContext(AppContext);

  const localPeer = useHMSStore(selectLocalPeer);
  const iamBot = localPeer?.roleName.toLowerCase() === ROLES.BOT;
  const iamGuest = localPeer?.roleName.toLowerCase() === ROLES.GUEST || loginInfo?.role === ROLES.GUEST;
  const fromBot = notification?.data?.roleName === ROLES.BOT;

  useEffect(() => {
    if (!notification) {
      return;
    }
    switch (notification.type) {
      case HMSNotificationTypes.PEER_LIST:
        console.debug("[Peer List]", notification.data);

        if (iamBot || fromBot) return;
        if (!subscribedNotifications.PEER_JOINED) return;

        const peers = notification.data?.filter(p => p.roleName !== ROLES.BOT);
        hmsToast("", {
          left: (
            <Text classes={{ root: "flex" }}>
              <PersonIcon className="mr-2" />
              {pluralize(peers?.length || 1, "peer")} joined
            </Text>
          ),
        });
        break;
      case HMSNotificationTypes.PEER_JOINED:
        console.debug("[Peer Joined]", notification.data);

        if (iamBot || fromBot) return;
        if (!subscribedNotifications.PEER_JOINED) return;

        hmsToast("", {
          left: (
            <Text classes={{ root: "flex" }}>
              <PersonIcon className="mr-2" />
              {notification.data?.name} joined
            </Text>
          ),
        });
        break;
      case HMSNotificationTypes.PEER_LEFT:
        console.debug("[Peer Left]", notification.data);

        if (iamBot || fromBot) return;
        if (!subscribedNotifications.PEER_LEFT) return;

        hmsToast("", {
          left: (
            <Text classes={{ root: "flex" }}>
              <PersonIcon className="mr-2" />
              {notification.data?.name} left
            </Text>
          ),
        });
        break;
      case HMSNotificationTypes.NEW_MESSAGE:
        if (iamBot || fromBot) return;
        // TODO: remove this when chat UI is fixed for mweb
        if (isMobileDevice()) {
          return;
        }
        if (!subscribedNotifications.NEW_MESSAGE) return;
        hmsToast(`New message from ${notification.data?.senderName}`);
        break;
      case HMSNotificationTypes.TRACK_ADDED:
        console.debug("[Track Added] data", notification.data);
        break;
      case HMSNotificationTypes.TRACK_REMOVED:
        console.debug("[Track Removed]", notification);
        break;
      case HMSNotificationTypes.TRACK_MUTED:
        console.log("[Track Muted]", notification);
        break;
      case HMSNotificationTypes.TRACK_UNMUTED:
        console.log("[Track Unmuted]", notification);
        break;
      case HMSNotificationTypes.METADATA_UPDATED:
        console.log("[Metadata Updated]", notification);
        break;
      case HMSNotificationTypes.ERROR:
        if (iamBot || fromBot) return;

        // show button action when the error is terminal
        if (!subscribedNotifications.ERROR) return;

        let message = notification.data?.message;
        let desc = notification.data?.description;

        if (iamGuest && message?.includes('Capture device is no longer available')) {
          return;
        }

        if (message && desc && !message.includes(desc)) {
          message += " - " + desc;
        }

        if (notification.data?.isTerminal) {
          if (notification.data?.code === 6008) {
            hmsToast("", {
              left: (
                <Text classes={{ root: "flex" }}>
                  {`Error: ${message}`}
                </Text>
              ),
              toastProps: {
                autoClose: false,
              },
            });
            setTimeout(() => {
              history.push("/");
            }, 2000);
            return;
          }

          hmsToast("", {
            center: (
              <div className="flex">
                <Text classes={{ root: "self-center mr-2" }}>
                  {message || "We couldn’t reconnect you. When you’re back online, try joining the room."}
                </Text>
                <Button
                  variant="emphasized"
                  classes={{
                    root: "self-center mr-2",
                  }}
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  Rejoin
                </Button>
              </div>
            ),
          });

          return;
        }

        if ([3001, 3003, 3008].includes(notification.data?.code)) {
          return;
        }

        hmsToast("", {
          left: (
            <Text classes={{ root: "flex" }}>
              {`Error: ${message}`}
            </Text>
          ),
        });
        break;
      case HMSNotificationTypes.RECONNECTED:
        if (iamBot || fromBot) return;

        hmsToast("", {
          left: (
            <Text classes={{ root: "flex" }}>
              <ConnectivityIcon className="mr-2" /> {iamBot ? 'Connection restored' : 'You are now connected'}
            </Text>
          ),
        });
        break;
      case HMSNotificationTypes.RECONNECTING:
        if (iamBot || fromBot) return;

        hmsToast("", {
          left: (
            <Text classes={{ root: "flex" }}>
              <PoorConnectivityIcon className="mr-2" /> You are offline for now.
              While we try to reconnect, please check your internet connection.
            </Text>
          )
        });

        break;
      case HMSNotificationTypes.ROLE_UPDATED:
        if (iamBot || fromBot) return;
        if (notification.data?.isLocal) {
          const newRole = notification.data.roleName;

          hmsToast("", {
            left: <Text>You are now a {newRole}.</Text>,
          });

          if (newRole === ROLES.GUEST) {
            hmsActions.setLocalAudioEnabled(false);
            hmsActions.setLocalVideoEnabled(false);
          }

          if ((newRole === ROLES.GUEST || newRole === ROLES.MEMBER) && isLocalScreenShared) {
            hmsActions.setScreenShareEnabled(false);
          }
        }
        break;
      case HMSNotificationTypes.CHANGE_TRACK_STATE_REQUEST:
        if (iamBot || fromBot) return;
        const track = notification.data?.track;
        if (!notification.data.enabled) {
          hmsToast("", {
            left: (
              <Text>
                Your {track.source} {track.type} was muted by{" "}
                {notification.data.requestedBy.name}.
              </Text>
            ),
          });
        }
        break;
      case HMSNotificationTypes.REMOVED_FROM_ROOM:
      case HMSNotificationTypes.ROOM_ENDED:
        if (!iamBot) {
          hmsToast("", {
            left: (
              <Text>
                {`${notification.message}. `}
                {notification.data.reason &&
                  `Reason: ${notification.data.reason}`}
              </Text>
            ),
          });
        }
        setTimeout(() => {
          if (params.authToken) {
            history.push("/leave/" + params.roomId + "/" + params.authToken);
          } else {
            history.push("/leave/" + params.roomId);
          }
        }, 2000);
        break;
      case HMSNotificationTypes.DEVICE_CHANGE_UPDATE:
        if (iamBot || fromBot) return;
        hmsToast("", {
          left: <Text>{notification.message}.</Text>,
        });
        break;
      default:
        break;
    }
  }, [hmsActions, notification]); //eslint-disable-line

  return (
    <>
      <HMSToastContainer />
      <TrackUnmuteModal notification={notification} />
      <TrackMuteAllModal notification={notification} />
      <AutoplayBlockedModal notification={notification} />
      <PermissionErrorModal notification={notification} />
    </>
  );
}
